import { redirect, replace, type LoaderFunctionArgs, type createBrowserRouter } from 'react-router-dom'
import { readUserScoped, writeUserScoped } from '@/utils/user-scoped-storage'

/**
 * "Where was I on this list" memory for every URL-backed list page (page,
 * page size, tab, filters, search, sort). The list pages already keep that
 * state in the URL, so Back from a detail page works — but any *fresh* entry
 * (sidebar, bottom nav, a save/delete redirect, Back fallback after a reload,
 * reopening the app) used the bare path and started over on page 1.
 *
 * This layer remembers each list's last query string per user in
 * localStorage (survives closing the app) and, when a list is entered with no
 * list params, redirects to the remembered URL inside the route loader —
 * before the page renders, so there is no default-page flash or double fetch.
 * Pages need no changes; "Clear filters" still works because the cleared URL
 * is what gets remembered next.
 */

const STORAGE_KEY = 'pmms.list-memory.v1'

/** Navigation hints a menu / bottom-nav link may carry that still mean "open my list". */
const HINT_KEYS = new Set(['tab', 'scope', 'new', 'page'])

/** One-shot flags — never replayed from memory. */
const TRANSIENT_KEYS = ['new', 'edit']

/**
 * Context deep links (a lead's quotations, a task reminder, a category's
 * designs, …). Such a visit is not the user's own list state, so it is
 * neither remembered nor overridden.
 */
const CONTEXT_KEYS = [
  'registeredLeadId',
  'salesLeadId',
  'lead',
  'leadId',
  'productId',
  'partyId',
  'businessName',
  'categoryId',
  'reminder',
  'dueToday',
  'personal',
]

type Memory = Record<string, string>

let cache: { key: string; memory: Memory } | null = null

function readMemory(): Memory {
  const raw = readUserScoped(STORAGE_KEY)
  if (cache?.key === raw) return cache.memory
  let memory: Memory = {}
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === 'object') memory = parsed as Memory
  } catch {
    // Corrupt entry — start fresh.
  }
  cache = { key: raw ?? '', memory }
  return memory
}

function remember(pathname: string, search: string) {
  const memory = readMemory()
  if ((memory[pathname] ?? '') === search) return
  const next = { ...memory }
  if (search) next[pathname] = search
  else delete next[pathname]
  const raw = JSON.stringify(next)
  writeUserScoped(STORAGE_KEY, raw)
  cache = { key: raw, memory: next }
}

function hasContextKey(params: URLSearchParams) {
  return CONTEXT_KEYS.some((key) => params.has(key))
}

/**
 * The URL to show instead of `pathname + search`, or null to keep it.
 * Exported for tests.
 */
export function restoredListUrl(pathname: string, search: string, stored: string | undefined): string | null {
  if (!stored) return null
  const incoming = new URLSearchParams(search)
  if (hasContextKey(incoming)) return null
  // A real filter / deep link in the URL wins as-is.
  if ([...incoming.keys()].some((key) => !HINT_KEYS.has(key))) return null

  const next = new URLSearchParams(stored)
  for (const key of ['tab', 'scope'] as const) {
    if (!incoming.has(key)) continue
    // Another stage than the remembered one starts on its first page.
    if ((next.get(key) ?? '') !== incoming.get(key)) next.delete('page')
    next.set(key, incoming.get(key)!)
  }
  if (incoming.has('page')) {
    const page = incoming.get('page')!
    if (Number(page) > 1) next.set('page', page)
    else next.delete('page')
  }
  if (incoming.has('new')) next.set('new', incoming.get('new')!)

  const qs = next.toString()
  if (qs === incoming.toString()) return null
  return qs ? `${pathname}?${qs}` : pathname
}

/** Route loader for list routes — restores the remembered list URL on entry. */
export function listMemoryLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const target = restoredListUrl(url.pathname, url.search, readMemory()[url.pathname])
  if (!target) return null
  // Back/Forward and cold starts are already in history — swap that entry
  // instead of stacking a second one. (Pushes are still pending here, so a
  // plain redirect lands as the single new entry.)
  const inHistory = window.location.pathname === url.pathname && window.location.search === url.search
  return inHistory ? replace(target) : redirect(target)
}

/** Only on entering the list — in-page filter / page changes never re-run it. */
export function listMemoryShouldRevalidate({ currentUrl, nextUrl }: { currentUrl: URL; nextUrl: URL }) {
  return currentUrl.pathname !== nextUrl.pathname
}

/** Save each list's URL whenever it settles on a remembered list path. */
export function installListMemory(router: ReturnType<typeof createBrowserRouter>, listPaths: readonly string[]) {
  const paths = new Set(listPaths)
  router.subscribe((state) => {
    if (state.navigation.state !== 'idle') return
    const { pathname, search } = state.location
    if (!paths.has(pathname)) return
    const params = new URLSearchParams(search)
    if (hasContextKey(params)) return
    for (const key of TRANSIENT_KEYS) params.delete(key)
    remember(pathname, params.toString())
  })
}
